/**
 * @file index.tsx
 *
 * @fileoverview Renders a full width Hero video banner.
 */
import React from 'react';
import { HeroVideoSearchBannerParagraph } from '~/jobiqo/types/types';
import styles from './heroVideoBanner.module.scss';
import { RowButtons } from '../RowButtons';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { JobSearchForm } from '~/components/Form/SearchInputForm';

interface HeroVideoBannerProps {
  /**
   * The row to display.
   */
  row: HeroVideoSearchBannerParagraph;
  /**
   * The max container size.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * The index of this row. Used to detect if its the first row on the page.
   */
  index: number;
}

export const HeroVideoBanner = ({
  row,
  maxContainerSize,
  index,
}: HeroVideoBannerProps) => {
  const overlayTransparency = row.overlayTransparency ?? 100;
  // Convert configured transparency to CSS opacity for the dark overlay layer.
  const overlayOpacity = 1 - overlayTransparency / 100;

  return (
    <div className={styles.videoWrapper}>
      <div className={styles.videoTopWrapper}>
        <video className={styles.videoBanner} autoPlay loop muted playsInline>
          <source src={row.videoUrl ?? ''} type="video/mp4" />
        </video>
      </div>
      <div
        className={styles.videoBannerWrapper}
        style={{ '--overlay-opacity': overlayOpacity } as React.CSSProperties}
      >
        <div className={styles.content}>
          <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
            <Text
              textAlign="center"
              tag={index === 0 ? 'h1' : 'h2'}
              fontSize={[6, 8]}
              lineHeight={[6, 7]}
            >
              {row.h1}
            </Text>
            <Spacing size="large" />
            <Text textAlign="center" fontSize={4} lineHeight={5}>
              {row.subText}
            </Text>
            <Spacing size="large" />
            <RowButtons
              button1={row.button1}
              button2={row.button2}
              buttonStyle={row.color as any}
            />
            {row.includeSearch && (
              <Spacing size="large">
                <JobSearchForm />
              </Spacing>
            )}
          </Main>
        </div>
      </div>
    </div>
  );
};
export default HeroVideoBanner;
